import React, { useEffect, useRef, useState } from "react";
import { DebitDirectConfirmation, Box, Spinner } from "@ui-kit/components/index.es";
import { connect } from "react-redux";
import { compose } from "redux";
import { arrayOf, func, shape, bool, string } from "prop-types";
import { resizableRoute } from "pages/_components/Resizable";
import Head from "pages/_components/Head";
import { push } from "react-router-redux";
import * as i18n from "util/i18n";
import Notification from "pages/_components/Notification";

import { selectors as frequentDestinationSelectors } from "reducers/frequentDestination";
import isTokenActive from "util/token";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import { actions as directDebitActions, selectors as directDebitSelectors } from "reducers/directDebit";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { selectors as sessionSelectors } from "reducers/session";
import moment from "moment";
import * as configUtils from "util/config";
import * as accountUtils from "util/accounts";
import Desktop from "pages/desktop/Desktop";
import Yup from "yup";
import { useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";

const ROW_PER_PAGE = 5;

const NewDirectDebitSummary = (props) => {
    const [accountName, setAccountName] = useState("");
    const [totalPages, setTotalPages] = useState(0);
    const [pageNumber, setPageNumber] = useState(0);
    const [dataDetails, setDataDetails] = useState([]);

    const {
        dispatch,
        accounts,
        initialValues,
        isDesktop,
        isFetching,
        fetchingToken,
        tokenNumber,
        loggedUser,
        dateUpload,
        embeddedInV3Shell,
    } = props;

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: "create.directDebit.title",
        goBackPath: "/v2/directDebit/create",
        scopes: ["directDebit"],
    });

    const goBack = useRef(false);

    useEffect(() => {
        if (!initialValues?.creditAccount) {
            dispatch(push("/v2/directDebit/create"));
        }
        if (!isDesktop && isTokenActive()) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
        if (!accounts.length > 0) {
            dispatch(accountsActions.listAccounts(loggedUser.userId));
        }
    }, []);

    useEffect(() => {
        if (Desktop) {
            setDataDetails(initialValues?.file?.details || []);
            setPageNumber(1);
            setTotalPages(Math.ceil((initialValues?.file?.details?.length || 0) / ROW_PER_PAGE));
        } else {
            setDataDetails(initialValues?.file?.details);
            setPageNumber(1);
            setTotalPages(1);
        }
    }, [initialValues?.file?.details]);

    useEffect(() => {
        if (initialValues?.creditAccount && accounts.length > 0) {
            const account = accounts.find((acc) => acc.cbu === initialValues.creditAccount);
            if (account) {
                setAccountName(accountUtils.getAccountName(account));
            }
        }
    }, [initialValues, accounts]);

    const handleBack = () => {
        goBack.current = true;
        dispatch(push("/v2/directDebit/create"));
    };

    const getSendDateFormat = (date) => {
        if (!date) {
            return "";
        }
        const hours = configUtils.get("time.limit.directDebitRequest.extract", "12");
        const tempDate = date && hours ? moment(date).set({ hour: hours, minute: "00", second: "00" }) : moment(date);
        const processDate = moment(date).isAfter(tempDate) ? tempDate.add(1, "days") : tempDate;
        return processDate.format("DD/MM/YYYY HH:mm");
    };

    const getToken = async () => {
        if (!isDesktop && isTokenActive() && tokenNumber) {
            return { token: tokenNumber };
        }
        return { token: null };
    };

    const getLabels = (id) => {
        const labels = {
            type: "Type",
            creditAccount: i18n.get("directDebit.creditAccount.actionConfirmation.label").toUpperCase(),
            fileReference: i18n.get("directDebit.reference.actionConfirmation.label").toUpperCase(),
            uploadDate: i18n.get("directDebit.loadDate.actionConfirmation.label").toUpperCase(),
            chargesOrdersCount: i18n.get("directDebit.debitOrders.actionConfirmation.label").toUpperCase(),
            totalAmount: i18n.get("directDebit.totalAmount.actionConfirmation.label").toUpperCase(),
            cbu: i18n.get("directDebit.cbu.detail.label"),
            dueDate: i18n.get("directDebit.dueDate.detail.label"),
            unequivocalReference: i18n.get("directDebit.unequivocalReference.detail.label"),
            reference: i18n.get("directDebit.reference.detail.label"),
            payroll: i18n.get("directDebit.payrollsNumber.actionConfirmation.label").toUpperCase(),
            fileName: i18n.get("directDebit.fileName.actionConfirmation.label").toUpperCase(),
            sendDate: i18n.get("directDebit.sendDate.actionConfirmation.label").toUpperCase(),
            viewDetails: i18n.get("directDebit.viewDetails.detail.label"),
            details: i18n.get("directDebit.details.detail.label"),
            close: i18n.get("directDebit.close.detail.label"),
            motivation: i18n.get("directDebit.motivation.detail.label"),
        };
        return labels[id] || id;
    };

    const otpSchema = Yup.object({
        otp:
            !isDesktop && isTokenActive() && tokenNumber
                ? Yup.string().nullable()
                : Yup.string().required(i18n.get("debin.programmed.confirmation.otp.required")),
    });

    const confirmationProps = {
        data: [
            { label: "Operation", value: "Direct Debit" },
            { label: "Items", value: "1" },
        ],
        title: "Confirmation",
        handleConfirm: ({ otp }) => {
            const finalOtp = !isDesktop && isTokenActive() && tokenNumber ? tokenNumber : otp;
            dispatch(directDebitActions.setGoBackDirectDebit(true));
            dispatch(
                directDebitActions.directDebitSend({
                    ...initialValues,
                    amountToShow: {
                        currency: initialValues?.file?.totalAmount?.currency,
                        quantity: initialValues?.file?.totalAmount?.bigQuantity,
                    },
                    _otp: finalOtp,
                }),
            );
        },

        initialValue: {
            otp: "",
        },
        loading: isFetching,
        validationConfirm: otpSchema,
        getToken,
    };
    return (
        <>
            {!embeddedInV3Shell &&
                (isDesktop ? (
                    <Notification scopeToShow="directDebit" />
                ) : (
                    <Notification scopeToShow="directDebitApp" />
                ))}
            <div className={`admin-detail-head ${isDesktop ? "px-6" : "px-3"}`}>
                {isDesktop ? (
                    <>
                        <Head onBack={handleBack} />
                        <Head title="create.directDebit.title" />
                    </>
                ) : (
                    !(embeddedInV3Shell && !isDesktop) && (
                        <Head
                            onBack={!isDesktop ? handleBack : null}
                            title="create.directDebit.title"
                            titleClassName={!isDesktop ? "header-mobile-title-background-blue overflow-visible" : ""}
                            headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                            hideNavbarInMobile
                        />
                    )
                )}

                {fetchingToken ? (
                    <Box flex justify="center" align="center" column py={72}>
                        <Spinner size="large" />
                    </Box>
                ) : (
                    <Box>
                        <DebitDirectConfirmation
                            type="Direct Debit"
                            uploadDate={moment(dateUpload).format("YYYY-MM-DD HH:mm")}
                            sendDate={getSendDateFormat(dateUpload)}
                            fileReference={initialValues?.reference || ""}
                            payroll={initialValues?.payrollsNumber?.label || ""}
                            fileName={
                                initialValues?.file?.newFileName?.split(".")[0] ||
                                initialValues?.file?.fileName?.split(".")[0] ||
                                ""
                            }
                            chargesOrdersCount={initialValues?.file?.validLines}
                            currency={initialValues?.file?.totalAmount?.currency}
                            amount={initialValues?.file?.totalAmount?.bigQuantity}
                            totalAmountTooltip={
                                isDesktop
                                    ? configUtils.get("frontend.DirectDebitRequest.TotalAmount.Tooltip")
                                    : undefined
                            }
                            data={
                                isDesktop
                                    ? dataDetails.slice((pageNumber - 1) * ROW_PER_PAGE, pageNumber * ROW_PER_PAGE)
                                    : dataDetails
                            }
                            paginationProps={{
                                totalPages,
                                pageNumber,
                                action: (pn) => setPageNumber(pn),
                            }}
                            getLabels={getLabels}
                            creditAccount={accountName}
                            confirmationProps={confirmationProps}
                            hintText={i18n.get("directDebit.actionConfirmation.hint")}
                            subtitle={i18n.get("create.summary.directDebit.actionConfirmation.subtitle")}
                            px={0}
                        />
                    </Box>
                )}
            </div>
        </>
    );
};

NewDirectDebitSummary.propTypes = {
    dispatch: func.isRequired,
    accounts: arrayOf(shape({})).isRequired,
    initialValues: shape({}).isRequired,
    isDesktop: bool.isRequired,
    isFetching: bool.isRequired,
    fetchingToken: bool.isRequired,
    tokenNumber: string,
    loggedUser: shape({}).isRequired,
    dateUpload: string,
    embeddedInV3Shell: bool,
};
NewDirectDebitSummary.defaultProps = {
    tokenNumber: null,
    dateUpload: null,
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    accounts: accountsSelectors.getAccounts(state),
    fetchingFrequentDestinations: frequentDestinationSelectors.getFetching(state),
    idEnvironment: frequentDestinationSelectors.getEnviromentId(state),
    listFrequentDestinations: frequentDestinationSelectors.getListFrequentDestinations(state),
    tokenNumber: tokenSelectors.getTokenNumber(state),
    fetchingToken: tokenSelectors.getFetchingToken(state),
    isFetching: directDebitSelectors.getFetchingList(state),
    initialValues: directDebitSelectors.getInitialValues(state),
    loggedUser: sessionSelectors.getUser(state),
    dateUpload: directDebitSelectors.getDateUpload(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(NewDirectDebitSummary);
